import React from "react";
import useRouter from "../../hooks/router";

const ACCENT = "#00c9a7";
const ACCENT2 = "#f7c94b";
const DARK_BG = "#111e27";
const CARD_BG = "#1a3347";
const NAV_BG = "#1a3a4a";
const TEXT_MAIN = "#e8f0f4";
const TEXT_MUTED = "#8fafc4";
const BORDER = "rgba(255,255,255,0.1)";

const posts = [
  {
    slug: "referee-pay-per-game",
    audience: "Referees",
    tag: "Pay",
    title: "How Much Do Soccer Referees Make Per Game?",
    excerpt: "A practical breakdown of per-game pay, what changes the rate, and what referees should ask before accepting assignments.",
    readTime: "6 min read",
    date: "March 2026",
    keywords: ["how much do soccer referees make", "referee pay per game", "soccer referee pay"],
    sections: [
      {
        heading: "What referee pay usually depends on",
        body: "Match level, age group, competition type, travel distance, and whether you are the center referee or an assistant all change the number. High school, youth club, adult amateur, and tournament matches often pay differently, and the schedule should make that obvious before you accept the game.",
      },
      {
        heading: "What referees should confirm before saying yes",
        body: "Ask for the location, kickoff time, expected duration, payment method, who is paying, and whether there are multiple games in one trip. Also confirm if any paperwork is required so you are not surprised after the assignment is complete.",
      },
      {
        heading: "What good assigners do",
        body: "Good assigners publish clear rates, communicate changes early, and avoid handing off games without pay details. That keeps officials available and reduces no-shows and late cancellations.",
      },
    ],
  },
  {
    slug: "find-soccer-referee-jobs",
    audience: "Referees",
    tag: "Jobs",
    title: "How to Find Soccer Referee Jobs Near You",
    excerpt: "If you are searching for referee work, the best opportunities usually come from local assigners, referee associations, and clubs that need reliable officials.",
    readTime: "7 min read",
    date: "March 2026",
    keywords: ["find soccer referee jobs", "referee jobs near me", "how to become a soccer referee"],
    sections: [
      {
        heading: "Where referee jobs actually come from",
        body: "Most game work starts with local assigners, state associations, club tournaments, school competitions, and referee groups. Search terms like 'referee jobs near me' often lead to directories, but the real work comes from being connected to the people assigning games.",
      },
      {
        heading: "What makes a referee more likely to get games",
        body: "Availability, communication, certification level, punctuality, and feedback history matter more than almost anything else. Assigners want officials who answer quickly, arrive early, and handle reschedules without drama.",
      },
      {
        heading: "How to get steady assignments",
        body: "Keep your availability updated, respond fast, accept games that match your level, and let assigners know where and when you can work. The officials who get the most games are usually the ones who are easiest to schedule.",
      },
    ],
  },
  {
    slug: "soccer-referee-rules-offside-handball-fouls",
    audience: "Referees",
    tag: "Rules",
    title: "Soccer Referee Rules: Offside, Handball, Fouls, and Game Management",
    excerpt: "A concise rules reference for referees who want practical clarity, not a giant wall of jargon.",
    readTime: "8 min read",
    date: "March 2026",
    keywords: ["soccer referee rules", "offside rule soccer", "handball rule", "foul interpretation"],
    sections: [
      {
        heading: "Offside is about involvement, not just position",
        body: "A player standing in an offside position is not automatically penalized. The key question is whether they become active in play, interfere with an opponent, or gain an advantage from that position. That distinction matters on fast restarts and rebounds.",
      },
      {
        heading: "Handball depends on more than contact",
        body: "Not every ball-to-hand contact is a handball. Referees should consider the position of the arm, whether the player made their body unnaturally bigger, and whether the movement toward the ball was deliberate or expected in the play.",
      },
      {
        heading: "Good foul decisions are consistent decisions",
        body: "The best referees do not just know the rule; they apply it consistently from the first minute to the last. Players and coaches will tolerate firm decisions far better than they tolerate inconsistent ones.",
      },
    ],
  },
  {
    slug: "soccer-referee-game-day-checklist",
    audience: "Referees",
    tag: "Preparation",
    title: "Soccer Referee Game Day Checklist: What to Bring, Check, and Confirm",
    excerpt: "A practical checklist for referees who want to show up prepared, avoid avoidable mistakes, and stay organized.",
    readTime: "5 min read",
    date: "February 2026",
    keywords: ["soccer referee checklist", "referee game day", "what to bring to a soccer game"],
    sections: [
      {
        heading: "Before you leave",
        body: "Confirm the field address, kickoff time, crew members, uniform color, and weather conditions. Leave early enough to deal with parking, field changes, or venue confusion.",
      },
      {
        heading: "What should be in your bag",
        body: "Carry cards, coin, whistle, watch, notepad, pens, extra socks, and water. The small things are what save you when the day gets messy.",
      },
      {
        heading: "Why preparation matters",
        body: "A prepared referee looks calm, professional, and trustworthy. That reputation leads to more assignments and fewer issues with assigners.",
      },
    ],
  },
  {
    slug: "how-to-find-referees",
    audience: "Assigners",
    tag: "Hiring",
    title: "How to Find Referees: A Practical Assigner's Guide",
    excerpt: "Where assigners actually find referees, how to build a pool, and what makes officials respond to your games.",
    readTime: "7 min read",
    date: "March 2026",
    keywords: ["how to find referees", "find soccer referees", "referee assigner guide"],
    sections: [
      {
        heading: "Where to recruit referees",
        body: "Start with local referee associations, existing officials who work nearby, school networks, club recommendations, and tournament pools. The best pool is usually built from a mix of new and experienced referees.",
      },
      {
        heading: "What referees want from an assigner",
        body: "Clear pay, early communication, reasonable travel expectations, and fast responses matter most. If your assignments are confusing or slow to update, referees will stop accepting them.",
      },
      {
        heading: "How to keep good referees active",
        body: "Give officials regular work, recognize reliable performance, and keep rules and pay details consistent. Referees stay engaged when the process feels fair and easy.",
      },
    ],
  },
  {
    slug: "assign-soccer-referees-efficiently",
    audience: "Assigners",
    tag: "Operations",
    title: "How to Assign Soccer Referees Efficiently Without Spreadsheet Chaos",
    excerpt: "A step-by-step assignment workflow for clubs and leagues trying to stay organized during busy seasons.",
    readTime: "8 min read",
    date: "February 2026",
    keywords: ["assign soccer referees", "referee assignment workflow", "sports scheduling"],
    sections: [
      {
        heading: "Assign in layers",
        body: "First filter by availability, then certification, then geography, then workload. This reduces backtracking and avoids filling the schedule with impossible matchups.",
      },
      {
        heading: "Protect your schedule from failures",
        body: "Always keep a backup list for critical games and tournament weekends. The difference between a smooth day and a crisis is usually whether you planned for a few cancellations.",
      },
      {
        heading: "Why the process matters",
        body: "A simple assignment workflow saves time, reduces errors, and makes your referee pool more responsive. People work better when the system is predictable.",
      },
    ],
  },
  {
    slug: "how-to-pay-soccer-referees",
    audience: "Assigners",
    tag: "Payments",
    title: "How to Pay Soccer Referees Correctly",
    excerpt: "A practical guide to rates, tracking, tax forms, and avoiding payment confusion later.",
    readTime: "9 min read",
    date: "February 2026",
    keywords: ["how to pay soccer referees", "referee payment process", "1099 referee pay"],
    sections: [
      {
        heading: "Payment should be obvious before the game",
        body: "The rate, method, and timing should be clear before anyone accepts the assignment. Hidden or inconsistent payment details create frustration and reduce coverage.",
      },
      {
        heading: "Track the full lifecycle",
        body: "Keep records of assigned games, completed games, corrections, and payouts. Good records make tax season and dispute resolution much easier.",
      },
      {
        heading: "Why payment quality affects coverage",
        body: "Officials are more likely to accept and keep coming back to organizations that pay reliably and communicate clearly. Payment reputation is part of your recruiting strategy.",
      },
    ],
  },
];

const Blog = () => {
  const { handleNavigation } = useRouter();

  return (
    <div style={{ background: DARK_BG }} className="font-outfit min-h-screen flex flex-col">
      <nav style={{ background: NAV_BG }} className="w-full px-4 sm:px-6 py-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between shadow-md sticky top-0 z-50">
        <img src="/logo.svg" alt="RefAlign" className="h-9 cursor-pointer self-center sm:self-auto" onClick={() => handleNavigation("/")} />
        <div className="flex flex-wrap justify-center sm:justify-end gap-3">
          <button onClick={() => handleNavigation("/pricing")} style={{ color: TEXT_MUTED, border: `1px solid ${BORDER}` }} className="px-4 py-2 rounded-lg text-sm hover:bg-white hover:bg-opacity-5 transition-all">Pricing</button>
          <button onClick={() => handleNavigation("/signup")} style={{ background: ACCENT, color: "#0d2230" }} className="px-4 py-2 rounded-lg text-sm font-semibold hover:opacity-90 transition-all">Get Started Free</button>
        </div>
      </nav>

      <section className="pt-20 pb-12 px-4 sm:px-6 text-center">
        <div style={{ background: "rgba(0,201,167,0.12)", color: ACCENT, border: "1px solid rgba(0,201,167,0.3)" }} className="inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-6">
          Referee + assigner resources
        </div>
        <h1 style={{ color: TEXT_MAIN }} className="text-4xl sm:text-5xl font-bold mb-4 max-w-4xl mx-auto leading-tight">
          High-intent blog posts for referees and assigners
        </h1>
        <p style={{ color: TEXT_MUTED }} className="text-base sm:text-lg max-w-3xl mx-auto leading-relaxed">
          Short, highly detailed posts built around the questions people are already searching for: referee pay, referee jobs, game rules, how to find referees, and how to assign games without chaos.
        </p>
      </section>

      <section className="px-4 sm:px-6 pb-8 max-w-7xl mx-auto w-full">
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {posts.map((post) => (
            <article key={post.slug} style={{ background: CARD_BG, border: `1px solid ${BORDER}` }} className="rounded-2xl p-6 sm:p-7 flex flex-col gap-4">
              <div className="flex flex-wrap items-center gap-2">
                <span style={{ background: "rgba(0,201,167,0.12)", color: ACCENT, border: "1px solid rgba(0,201,167,0.28)" }} className="px-3 py-1 rounded-full text-xs font-semibold">{post.audience}</span>
                <span style={{ background: `${ACCENT2}18`, color: ACCENT2, border: `1px solid ${ACCENT2}33` }} className="px-3 py-1 rounded-full text-xs font-semibold">{post.tag}</span>
                <span style={{ color: TEXT_MUTED }} className="text-xs">{post.date} · {post.readTime}</span>
              </div>

              <div>
                <h2 style={{ color: TEXT_MAIN }} className="text-2xl font-bold leading-tight mb-2">{post.title}</h2>
                <p style={{ color: TEXT_MUTED }} className="text-sm leading-relaxed">{post.excerpt}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${BORDER}` }} className="rounded-xl p-4">
                  <p style={{ color: ACCENT }} className="text-xs font-bold uppercase tracking-wide mb-2">Target keywords</p>
                  <div className="flex flex-col gap-2">
                    {post.keywords.map((k) => (
                      <div key={k} style={{ color: TEXT_MUTED }} className="text-xs leading-relaxed">{k}</div>
                    ))}
                  </div>
                </div>
                <div style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${BORDER}` }} className="rounded-xl p-4">
                  <p style={{ color: ACCENT2 }} className="text-xs font-bold uppercase tracking-wide mb-2">What readers get</p>
                  <div style={{ color: TEXT_MUTED }} className="text-xs leading-relaxed">
                    Practical guidance, clear examples, and enough detail to help someone make a real decision without reading a giant wall of text.
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-4">
                {post.sections.map((section) => (
                  <div key={section.heading} style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${BORDER}` }} className="rounded-xl p-4">
                    <h3 style={{ color: TEXT_MAIN }} className="text-sm font-bold mb-2">{section.heading}</h3>
                    <p style={{ color: TEXT_MUTED }} className="text-sm leading-relaxed">{section.body}</p>
                  </div>
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section style={{ background: "linear-gradient(135deg, #00c9a7 0%, #0091a7 100%)" }} className="py-16 px-4 sm:px-6 text-center mt-auto">
        <h2 className="text-3xl font-bold text-white mb-3">Ready to manage referee assignments better?</h2>
        <p className="text-white opacity-90 mb-8 text-base sm:text-lg max-w-2xl mx-auto">If you want a simpler, faster way to handle games, payments, and communication, get started now.</p>
        <button onClick={() => handleNavigation("/signup")} style={{ background: "#0d2230", color: TEXT_MAIN }} className="px-10 py-4 rounded-xl font-bold hover:opacity-90 transition-all shadow-xl">
          Get Started Free →
        </button>
      </section>
    </div>
  );
};

export default Blog;
